import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard, Plus, X as XIcon, CurrencyDollar, ArrowClockwise, CheckCircle,
  Warning, Receipt, Bank, Upload, UploadSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";

const PLAN_PRICING = {
  solo:  { monthly: 79,  annual: 790 },
  pro:   { monthly: 149, annual: 1490 },
  fleet: { monthly: 299, annual: 2990 },
};
const PLAN_LABELS = { solo: "Solo", pro: "Pro", fleet: "Fleet" };
const METHOD_LABELS = {
  mercury_ach: "Mercury ACH",
  mercury_wire: "Mercury Wire",
  check: "Check",
  cash: "Cash",
  other: "Other",
};

function fmtUSD(n) { return `$${Math.round(n || 0).toLocaleString()}`; }

function MercuryImportModal({ open, onClose, onImported, trialUsers }) {
  const [csvText, setCsvText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [deposits, setDeposits] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const [overrides, setOverrides] = useState({});  // per dedupe_key: { user_id, plan, period_months }

  useEffect(() => {
    if (open) { setCsvText(""); setDeposits(null); setOverrides({}); }
  }, [open]);

  if (!open) return null;

  const parse = async () => {
    if (!csvText.trim()) {
      toast.error("Paste your Mercury CSV first");
      return;
    }
    setParsing(true);
    try {
      const { data } = await client.post("/admin/mercury/reconcile", { csv_text: csvText });
      setDeposits(data.deposits || []);
      if (data.deposits.length === 0) {
        toast.info(data.message || "No deposits found in CSV");
      } else {
        toast.success(`Parsed ${data.total_deposits} deposits — ${data.high_confidence} high-confidence matches`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const activate = async (dep) => {
    const override = overrides[dep.dedupe_key] || {};
    const userId = override.user_id ?? dep.matched_user?.id;
    const plan = override.plan ?? dep.suggested_plan;
    const period = override.period_months ?? dep.suggested_period_months ?? 1;
    if (!userId) {
      toast.error("Pick a user first");
      return;
    }
    if (!plan) {
      toast.error("Pick a plan first");
      return;
    }
    setImportingId(dep.dedupe_key);
    try {
      const { data } = await client.post("/admin/mercury/import-and-activate", {
        dedupe_key: dep.dedupe_key,
        amount: dep.amount,
        date: dep.date,
        description: dep.description,
        reference: dep.reference,
        user_id: userId,
        plan,
        period_months: period,
      });
      toast.success(`Activated — ${data.user_email} on ${plan}`);
      // Mark this deposit as imported in local state
      setDeposits((prev) => prev.map((d) =>
        d.dedupe_key === dep.dedupe_key ? { ...d, already_imported: true } : d
      ));
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  const setOverride = (key, patch) =>
    setOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const highCount = deposits ? deposits.filter((d) => d.confidence === "high" && !d.already_imported).length : 0;
  const totalNew = deposits ? deposits.filter((d) => !d.already_imported).length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="mercury-import-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-4xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                Mercury reconciliation
              </div>
              <div className="text-sm font-medium text-white">Import CSV → auto-match trial users</div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1"
                    data-testid="mercury-modal-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {!deposits && (
              <>
                <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-3 flex gap-2">
                  <Warning size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-amber-200/80">
                    In Mercury: go to <b>Transactions → Export</b>, choose "All time" or a recent window,
                    download the CSV, then paste it below. Only credit-side deposits are considered.
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                    Paste Mercury CSV
                  </div>
                  <textarea
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    rows={10}
                    placeholder="Date,Description,Amount,Status,Reference..."
                    className="w-full rounded-md bg-black/40 border border-white/10 text-white text-xs px-3 py-2 focus:border-[#FF5A1F] outline-none font-mono"
                    data-testid="mercury-csv-input"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <label className="rounded-full px-4 py-2 text-xs border border-white/15 hover:border-white/30 text-neutral-300 hover:text-white inline-flex items-center gap-1.5 cursor-pointer">
                    <UploadSimple size={12} /> Upload file
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      data-testid="mercury-csv-file"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const txt = await f.text();
                        setCsvText(txt);
                        toast.success(`Loaded ${f.name}`);
                      }}
                    />
                  </label>
                  <button onClick={parse} disabled={parsing || !csvText.trim()}
                    data-testid="mercury-parse-btn"
                    className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
                    {parsing ? <><ArrowClockwise size={12} className="animate-spin" /> Parsing…</> :
                      <><Upload size={12} weight="bold" /> Parse deposits</>}
                  </button>
                </div>
              </>
            )}

            {deposits && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-neutral-400">
                    <span className="text-white font-semibold">{totalNew}</span> new deposits to review
                    {highCount > 0 && <span className="text-emerald-400 ml-2">· {highCount} high-confidence</span>}
                    <span className="text-neutral-600 ml-2">· {deposits.length - totalNew} already imported</span>
                  </div>
                  <button onClick={() => setDeposits(null)}
                    data-testid="mercury-back-btn"
                    className="rounded-full px-3 py-1.5 text-xs border border-white/15 text-neutral-400 hover:text-white">
                    ← Paste new CSV
                  </button>
                </div>

                {deposits.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 py-8 text-center text-sm text-neutral-500">
                    No credit-side deposits found. Only positive-amount rows are matched.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="mercury-deposits-list">
                    {deposits.map((dep) => {
                      const override = overrides[dep.dedupe_key] || {};
                      const effectiveUser = override.user_id
                        ? trialUsers.find((u) => u.id === override.user_id)
                        : dep.matched_user;
                      const effectivePlan = override.plan ?? dep.suggested_plan;
                      const effectivePeriod = override.period_months ?? dep.suggested_period_months ?? 1;
                      const canActivate = effectiveUser && effectivePlan && !dep.already_imported;
                      const conf = dep.confidence;
                      return (
                        <div key={dep.dedupe_key}
                             className={`rounded-lg border p-3 space-y-2 ${
                               dep.already_imported ? "border-white/5 bg-white/[0.01] opacity-50" :
                               conf === "high" ? "border-emerald-500/30 bg-emerald-500/5" :
                               conf === "medium" ? "border-amber-500/20 bg-amber-500/[0.03]" :
                               "border-white/10 bg-white/[0.02]"
                             }`}
                             data-testid={`mercury-deposit-${dep.dedupe_key.replace(/[^a-zA-Z0-9-]/g, "_")}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-display font-semibold text-emerald-400 tabular-nums">
                                  {fmtUSD(dep.amount)}
                                </span>
                                <span className="text-xs text-neutral-500">{dep.date}</span>
                                {dep.already_imported && (
                                  <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">
                                    already imported
                                  </span>
                                )}
                                {!dep.already_imported && conf === "high" && (
                                  <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-semibold">
                                    HIGH CONFIDENCE
                                  </span>
                                )}
                                {!dep.already_imported && conf === "medium" && (
                                  <span className="text-[9px] uppercase tracking-widest text-amber-400 font-semibold">
                                    NEEDS PICK
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-neutral-400 truncate mt-0.5">{dep.description}</div>
                              {dep.reference && <div className="text-[10px] text-neutral-600 font-mono">ref: {dep.reference}</div>}
                              {dep.match_reason && (
                                <div className="text-[10px] text-emerald-400/70 mt-1">
                                  Matched: {dep.match_reason}
                                </div>
                              )}
                            </div>
                          </div>

                          {!dep.already_imported && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2 border-t border-white/[0.06]">
                              <select
                                value={effectiveUser?.id || ""}
                                onChange={(e) => setOverride(dep.dedupe_key, { user_id: e.target.value })}
                                data-testid={`mercury-user-select-${dep.dedupe_key.replace(/[^a-zA-Z0-9-]/g, "_")}`}
                                className="rounded-md bg-black/40 border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#FF5A1F] outline-none"
                              >
                                <option value="">— Pick user —</option>
                                {trialUsers.map((u) => (
                                  <option key={u.id} value={u.id} className="bg-black">
                                    {u.email} {u.company_name ? `· ${u.company_name}` : ""}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={effectivePlan || ""}
                                onChange={(e) => setOverride(dep.dedupe_key, { plan: e.target.value })}
                                className="rounded-md bg-black/40 border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#FF5A1F] outline-none"
                              >
                                <option value="">— Pick plan —</option>
                                <option value="solo">Solo ({effectivePeriod === 12 ? "$790/yr" : "$79/mo"})</option>
                                <option value="pro">Pro ({effectivePeriod === 12 ? "$1,490/yr" : "$149/mo"})</option>
                                <option value="fleet">Fleet ({effectivePeriod === 12 ? "$2,990/yr" : "$299/mo"})</option>
                              </select>
                              <button
                                onClick={() => activate(dep)}
                                disabled={!canActivate || importingId === dep.dedupe_key}
                                data-testid={`mercury-activate-${dep.dedupe_key.replace(/[^a-zA-Z0-9-]/g, "_")}`}
                                className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                              >
                                {importingId === dep.dedupe_key ? (
                                  <><ArrowClockwise size={11} className="animate-spin" /> Activating…</>
                                ) : (
                                  <><CheckCircle size={11} weight="fill" /> Activate</>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivateModal({ open, onClose, onActivated, trialUsers }) {
  const [step, setStep] = useState(1);  // 1=pick user 2=plan+method 3=confirm
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [plan, setPlan] = useState("solo");
  const [period, setPeriod] = useState(1);  // 1 or 12
  const [method, setMethod] = useState("mercury_ach");
  const [externalRef, setExternalRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1); setSelectedUser(null); setSearch("");
      setPlan("solo"); setPeriod(1); setMethod("mercury_ach");
      setExternalRef(""); setNotes("");
    }
  }, [open]);

  if (!open) return null;

  const filteredUsers = trialUsers.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) ||
           (u.full_name || "").toLowerCase().includes(q) ||
           (u.company_name || "").toLowerCase().includes(q);
  });

  const amount = PLAN_PRICING[plan][period === 12 ? "annual" : "monthly"];

  const submit = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      const { data } = await client.post("/admin/subscriptions/activate-manual", {
        user_id: selectedUser.id,
        plan,
        period_months: period,
        amount,
        payment_method: method,
        external_ref: externalRef || undefined,
        notes: notes || undefined,
      });
      if (data.status === "activated") {
        toast.success(`${selectedUser.email} activated on ${PLAN_LABELS[plan]} until ${new Date(data.expires_at).toLocaleDateString()}`);
        onActivated?.();
        onClose();
      } else {
        toast.error("Activation failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="activate-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                Manual activation · step {step} of 3
              </div>
              <div className="text-sm font-medium text-white">
                {step === 1 && "Pick trial user"}
                {step === 2 && "Plan + payment"}
                {step === 3 && "Confirm & activate"}
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1"
                    data-testid="activate-modal-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* STEP 1 */}
            {step === 1 && (
              <>
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email, name, or company…"
                  className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none"
                  data-testid="activate-search-input"
                />
                {trialUsers.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-neutral-500">
                    No trial users right now.
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-4 text-center">
                    No match for "{search}"
                  </div>
                ) : (
                  <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="activate-user-list">
                    {filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => { setSelectedUser(u); setStep(2); }}
                        data-testid={`activate-pick-${u.id}`}
                        className="w-full text-left rounded-md border border-white/10 hover:border-[#FF5A1F]/50 hover:bg-white/[0.03] p-3 transition-colors"
                      >
                        <div className="text-sm text-white truncate">
                          {u.full_name || "—"} · <span className="text-neutral-400">{u.company_name || "no company"}</span>
                        </div>
                        <div className="text-xs text-neutral-500 font-mono truncate">{u.email}</div>
                        <div className="text-[10px] text-neutral-600 mt-1">
                          {u.subscription_status} · signed up {u.created_at ? new Date(u.created_at).toLocaleDateString() : "?"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* STEP 2 */}
            {step === 2 && selectedUser && (
              <>
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 text-xs">
                  <div className="text-neutral-500 mb-0.5">Activating for</div>
                  <div className="text-sm text-white font-medium truncate">
                    {selectedUser.full_name || "—"} · {selectedUser.company_name || "no company"}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono truncate">{selectedUser.email}</div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Plan</div>
                  <div className="grid grid-cols-3 gap-2" data-testid="activate-plan-picker">
                    {Object.keys(PLAN_PRICING).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPlan(p)}
                        data-testid={`activate-plan-${p}`}
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          plan === p ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white" : "border-white/10 hover:border-white/25 text-neutral-400"
                        }`}
                      >
                        <div className="text-sm font-semibold">{PLAN_LABELS[p]}</div>
                        <div className="text-[11px] mt-0.5 opacity-70">
                          ${PLAN_PRICING[p].monthly}/mo · ${PLAN_PRICING[p].annual}/yr
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Period</div>
                  <div className="flex items-center gap-2" data-testid="activate-period-picker">
                    <button
                      onClick={() => setPeriod(1)}
                      data-testid="activate-period-monthly"
                      className={`flex-1 rounded-lg border p-2.5 text-xs transition-colors ${
                        period === 1 ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white" : "border-white/10 text-neutral-400"
                      }`}
                    >
                      Monthly · ${PLAN_PRICING[plan].monthly}
                    </button>
                    <button
                      onClick={() => setPeriod(12)}
                      data-testid="activate-period-annual"
                      className={`flex-1 rounded-lg border p-2.5 text-xs transition-colors ${
                        period === 12 ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white" : "border-white/10 text-neutral-400"
                      }`}
                    >
                      Annual · ${PLAN_PRICING[plan].annual} <span className="text-emerald-400">(save 2 mo)</span>
                    </button>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Payment method</div>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    data-testid="activate-method-select"
                    className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none"
                  >
                    {Object.entries(METHOD_LABELS).map(([k, v]) => (
                      <option key={k} value={k} className="bg-black">{v}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">External ref (optional)</div>
                  <input
                    type="text"
                    value={externalRef}
                    onChange={(e) => setExternalRef(e.target.value)}
                    placeholder="Mercury txn ID, check #, wire ref…"
                    className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none font-mono"
                    data-testid="activate-external-ref-input"
                  />
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                  <button onClick={() => setStep(1)}
                    className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-400 hover:text-white">
                    ← Back
                  </button>
                  <button onClick={() => setStep(3)}
                    data-testid="activate-continue-btn"
                    className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2">
                    Continue →
                  </button>
                </div>
              </>
            )}

            {/* STEP 3 */}
            {step === 3 && selectedUser && (
              <>
                <div className="rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 p-4 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                    Ready to activate
                  </div>
                  <div className="text-sm text-white">
                    {selectedUser.email} → <b>{PLAN_LABELS[plan]}</b> plan
                  </div>
                  <div className="text-lg font-display font-semibold text-white">
                    {fmtUSD(amount)} · {period === 12 ? "annual" : "monthly"}
                  </div>
                  <div className="text-xs text-neutral-400">
                    Paid via {METHOD_LABELS[method]}{externalRef ? ` · ref ${externalRef}` : ""}
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    Access valid until {new Date(Date.now() + 30 * 24 * 3600 * 1000 * period).toLocaleDateString()}
                  </div>
                </div>

                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Internal notes (optional)"
                  className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none"
                  data-testid="activate-notes-input"
                />

                <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-3 flex gap-2">
                  <Warning size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-amber-200/80">
                    A receipt email will be sent to <span className="font-mono">{selectedUser.email}</span> confirming
                    the activation. Only proceed after you've verified the payment in Mercury/your bank.
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                  <button onClick={() => setStep(2)} disabled={submitting}
                    className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-400 hover:text-white">
                    ← Back
                  </button>
                  <button onClick={submit} disabled={submitting}
                    data-testid="activate-confirm-btn"
                    className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
                    {submitting ? <><ArrowClockwise size={12} className="animate-spin" /> Activating…</> :
                      <><CheckCircle size={12} weight="fill" /> Confirm activation</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminManualPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [trialUsers, setTrialUsers] = useState([]);
  const [totals, setTotals] = useState({ active_count: 0, total_active_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [mercuryOpen, setMercuryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        client.get("/admin/subscriptions/manual-payments"),
        client.get("/admin/subscriptions/trial-users"),
      ]);
      setPayments(p.data.payments || []);
      setTotals({
        active_count: p.data.active_count || 0,
        total_active_amount: p.data.total_active_amount || 0,
      });
      setTrialUsers(t.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (payment) => {
    if (!window.confirm(`Cancel ${payment.user_email}'s ${payment.plan} activation and revert to trial? (Refunds must be issued separately outside the app.)`)) return;
    try {
      await client.post(`/admin/subscriptions/cancel-manual/${payment.id}`);
      toast.success("Activation canceled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-manual-payments-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">MANUAL PAYMENTS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Off-Stripe activations
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Activate paid subscriptions without Stripe — collect via Mercury ACH, wire, or check.
            Confirm the payment landed in your bank, then activate here to unlock the customer's account
            and email them a receipt.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setMercuryOpen(true)}
            data-testid="mercury-open-btn"
            className="rounded-full px-4 py-2 text-xs border border-white/15 hover:border-white/30 text-neutral-300 hover:text-white inline-flex items-center gap-1.5">
            <UploadSimple size={12} /> Import Mercury CSV
          </button>
          <button onClick={() => setModalOpen(true)}
            data-testid="activate-open-btn"
            className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2">
            <Plus size={12} weight="bold" /> Activate a trial user
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="manual-payments-stats">
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Active activations</div>
          <div className="font-display text-3xl font-semibold text-emerald-400 tabular-nums">
            {totals.active_count}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">non-Stripe</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">Active MRR (est.)</div>
          <div className="font-display text-3xl font-semibold text-[#FF5A1F] tabular-nums">
            {fmtUSD(totals.total_active_amount)}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">from manual payments</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Trial users eligible</div>
          <div className="font-display text-3xl font-semibold text-white tabular-nums">
            {trialUsers.length}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">not yet paid</div>
        </div>
      </div>

      {/* Payments list */}
      {loading ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading…</div>
      ) : payments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500">
          <Bank size={20} className="mx-auto mb-2 text-neutral-600" />
          <div>No manual activations yet.</div>
          <div className="mt-1 text-[11px]">
            Click "Activate a trial user" once payment has landed in Mercury.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="manual-payments-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-3 py-3">Plan</th>
                  <th className="text-left px-3 py-3">Method</th>
                  <th className="text-right px-3 py-3">Amount</th>
                  <th className="text-left px-3 py-3">Activated</th>
                  <th className="text-left px-3 py-3">Expires</th>
                  <th className="text-right px-3 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {payments.map((p) => {
                  const isActive = p.status === "active";
                  return (
                    <tr key={p.id} className="hover:bg-white/[0.02]"
                        data-testid={`manual-payment-${p.id}`}>
                      <td className="text-left px-4 py-3">
                        <div className="text-white text-sm font-medium truncate max-w-[220px]">
                          {p.user_email}
                        </div>
                        {p.external_ref && (
                          <div className="text-[10px] text-neutral-500 font-mono truncate max-w-[220px]">
                            ref: {p.external_ref}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-white font-medium">{PLAN_LABELS[p.plan] || p.plan}</span>
                        <span className="text-[10px] text-neutral-500 ml-1">
                          {p.period_months === 12 ? "yr" : "mo"}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-neutral-400">
                        {METHOD_LABELS[p.payment_method] || p.payment_method}
                      </td>
                      <td className="text-right px-3 py-3 tabular-nums text-emerald-400 font-medium">
                        {fmtUSD(p.amount)}
                      </td>
                      <td className="px-3 py-3 text-xs text-neutral-400 whitespace-nowrap">
                        {new Date(p.activated_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3 text-xs text-neutral-400 whitespace-nowrap">
                        {new Date(p.expires_at).toLocaleDateString()}
                      </td>
                      <td className="text-right px-3 py-3">
                        {isActive ? (
                          <button onClick={() => cancel(p)}
                            data-testid={`manual-cancel-${p.id}`}
                            className="text-xs text-red-400 hover:text-red-300">
                            Cancel
                          </button>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-neutral-600">
                            {p.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ActivateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onActivated={load}
        trialUsers={trialUsers}
      />
      <MercuryImportModal
        open={mercuryOpen}
        onClose={() => setMercuryOpen(false)}
        onImported={load}
        trialUsers={trialUsers}
      />
    </div>
  );
}
